import { Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router";
import { apiSend, useApi } from "../../api";
import type { PortalConfig } from "../../config";
import { Modal } from "../ticket/Modal";
import { RangePicker, Tile, usd, useRange, type Range } from "./common";

interface Watchmaker {
  id: string; name: string; firstName: string | null; lastName: string | null; email: string | null; phone: string | null; active: boolean;
  openNow: number; byStatus: { status: string; count: number }[]; overdue: number;
  paidTickets: number; sales: number; expenses: number; profit: number; finished: number; avgDays: number | null;
}
type Form = { businessName: string; firstName: string; lastName: string; email: string; phone: string; active: boolean };
const blank: Form = { businessName: "", firstName: "", lastName: "", email: "", phone: "", active: true };

/**
 * Watchmakers (owner, 2026-11-04; old Watchmakers page): each watchmaker (ticket location) with the work they hold
 * now, and for the range: tickets paid, sales, expenses, profit, tickets finished and average days. Add and edit
 * watchmakers here.
 */
export function WatchmakersPage({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [range, setRange] = useRange();
  const [reload, setReload] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const { data, error } = useApi<{ range: Range; watchmakers: Watchmaker[] }>(config, `/reports/watchmakers?from=${range.from}&to=${range.to}`, reload);
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<Form>(blank);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const all = data?.watchmakers ?? [];
  const list = all.filter((w) => showInactive || w.active || w.openNow > 0 || w.paidTickets > 0);
  const sum = (f: (w: Watchmaker) => number) => all.reduce((s, w) => s + f(w), 0);

  const open = (w: Watchmaker | null) => {
    setProblem(null);
    setEditing(w ? w.id : "new");
    setForm(w ? { businessName: w.name, firstName: w.firstName ?? "", lastName: w.lastName ?? "", email: w.email ?? "", phone: w.phone ?? "", active: w.active } : blank);
  };
  const save = async () => {
    setBusy(true); setProblem(null);
    try {
      await apiSend(config, token, editing === "new" ? "POST" : "PUT", editing === "new" ? "/watchmakers" : `/watchmakers/${editing}`, form);
      setEditing(null); setReload((n) => n + 1);
    } catch (e) { setProblem((e as Error).message); } finally { setBusy(false); }
  };
  const field = (k: keyof Omit<Form, "active">, label: string, type = "text") => (
    <label>{label}<input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></label>
  );

  return (
    <div className="report-page">
      <div className="report-head"><h2>Watchmakers</h2>
        <button type="button" className="primary" onClick={() => open(null)}><Plus size={15} /> Add watchmaker</button>
      </div>
      <RangePicker range={range} onChange={setRange}>
        <label className="check"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive</label>
      </RangePicker>
      {error && <p className="error">{error.message}</p>}
      <div className="tiles">
        <Tile label="Open with watchmakers" value={sum((w) => w.openNow)} hint="Open tickets right now" />
        <Tile label="Overdue" value={sum((w) => w.overdue)} invert hint="Open tickets past their due date" />
        <Tile label="Finished in range" value={sum((w) => w.finished)} />
        <Tile label="Sales in range" value={sum((w) => w.sales)} money />
        <Tile label="Profit in range" value={sum((w) => w.profit)} money hint="Sales less expenses of the tickets paid in the range" />
      </div>
      {!data && !error && <p className="muted">Loading…</p>}
      <div className="wm-grid">
        {list.map((w) => (
          <section key={w.id} className={`panel wm-card${w.active ? "" : " inactive"}`}>
            <header>
              <div><h3>{w.name}</h3>
                <p className="muted small">{[[w.firstName, w.lastName].filter(Boolean).join(" "), w.email, w.phone, w.active ? "" : "Inactive"].filter((x) => x && x !== w.name).join(" · ")}</p></div>
              <button type="button" className="icon-link" aria-label={`Edit ${w.name}`} onClick={() => open(w)}><Pencil size={15} /></button>
            </header>
            <div className="wm-now">
              <Link to={`/tickets?location=${w.id}&view=all`} className="wm-big">{w.openNow}<small>open now</small></Link>
              {w.overdue > 0 && <span className="pill warn">{w.overdue} overdue</span>}
              <div className="chips">{w.byStatus.map((s) => <span key={s.status} className="chip">{s.status} <strong>{s.count}</strong></span>)}</div>
            </div>
            <dl className="wm-stats">
              <div><dt>Paid tickets</dt><dd>{w.paidTickets}</dd></div>
              <div><dt>Sales</dt><dd>{usd(w.sales, false)}</dd></div>
              <div><dt>Expenses</dt><dd>{usd(w.expenses, false)}</dd></div>
              <div><dt>Profit</dt><dd className={w.profit < 0 ? "neg" : ""}>{usd(w.profit, false)}</dd></div>
              <div><dt>Finished</dt><dd>{w.finished}</dd></div>
              <div><dt>Avg days</dt><dd>{w.avgDays ?? "—"}</dd></div>
            </dl>
          </section>
        ))}
        {data && !list.length && <p className="muted">No watchmakers yet.</p>}
      </div>
      {editing && (
        <Modal title={editing === "new" ? "Add watchmaker" : "Edit watchmaker"} onClose={() => setEditing(null)}>
          <form className="stack-form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
            {field("businessName", "Name (shown as the ticket location)")}
            <div className="two">{field("firstName", "First name")}{field("lastName", "Last name")}</div>
            <div className="two">{field("email", "Email", "email")}{field("phone", "Phone", "tel")}</div>
            <label className="check"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
            {problem && <p className="error">{problem}</p>}
            <div className="btn-row between"><button type="button" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy || !form.businessName.trim()}>{busy ? "Saving…" : "Save"}</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
